import { DENOMINATIONS, useShift } from '../../store/shift';
import { amountStep, amountText } from '../../store/currency';
import { useUI } from '../../store/ui';
import { useTheme } from '../../store/theme';
import { useAuth } from '@matix/auth';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { CheckIcon, MoonIcon, SunIcon, LogOutIcon } from '../../components/icons';

/**
 * Shift-open screen — fullscreen takeover after login, before main POS.
 *
 * Two count modes:
 *   - denom: grid of SAR denominations × counts → live subtotals
 *   - single: type the total directly
 *
 * Submits with `confirmOpenShift` (currently local-only; will call shiftAPI
 * when feature 3 ships).
 */
export function ShiftOpenScreen() {
  const {
    openMode, openCounts, openSingleAmount, openNotes, openTotal,
    setOpenMode, setOpenCount, setOpenSingleAmount, setOpenNotes,
    confirmOpenShift,
  } = useShift();
  const setScreen = useUI((s) => s.setScreen);
  const themeMode = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);
  const { logout, cashier } = useAuth();
  const { t } = useLang();

  const total = openTotal();
  const username = cashier?.username ?? 'Cashier';

  function handleConfirm() {
    // Allow opening a shift with 0 opening cash (empty drawer). Only a
    // negative total is invalid — which the count inputs already prevent.
    if (total < 0) return;
    confirmOpenShift();
    toast(
      t({
        en: `Shift opened · ${fmt(total)} opening cash`,
        ar: `تم فتح الوردية · ${fmt(total)} نقدية افتتاحية`,
      }),
      'success',
    );
    setScreen('app');
  }

  return (
    <div className="fullscreen" id="shiftOpenScreen">
      <header className="topbar">
        <div className="topbar-title">{t({ en: 'Open shift', ar: 'فتح الوردية' })}</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 16 }}>
          {t({ en: `Welcome back, ${username}`, ar: `مرحبًا بعودتك، ${username}` })}
        </div>
        <div className="topbar-spacer" />
        <button
          className="btn-secondary"
          onClick={() => setScreen('app')}
          style={{ marginRight: 8 }}
        >
          {t({ en: 'Cancel', ar: 'إلغاء' })}
        </button>
        <button className="theme-toggle" onClick={toggleTheme} title={t({ en: 'Toggle theme', ar: 'تبديل المظهر' })}>
          {themeMode === 'light' ? <MoonIcon size={20} /> : <SunIcon size={20} />}
        </button>
        <button
          className="icon-btn"
          onClick={() => void logout()}
          title={t({ en: 'Sign out', ar: 'تسجيل الخروج' })}
          style={{ marginLeft: 8 }}
        >
          <LogOutIcon size={20} />
        </button>
      </header>

      <div className="shift-header">
        <div>
          <div className="shift-header-title">{t({ en: 'Count opening cash', ar: 'عدّ النقدية الافتتاحية' })}</div>
          <div className="shift-header-sub">
            {t({ en: "Count what's in the drawer before your first sale.", ar: 'عُدّ ما في الدرج قبل أول عملية بيع.' })}
          </div>
        </div>
      </div>

      <div className="shift-screen">
        <div className="shift-main">
          <div className="shift-mode-toggle">
            <button
              className={openMode === 'denom' ? 'active' : ''}
              onClick={() => setOpenMode('denom')}
            >
              {t({ en: 'Count by denomination', ar: 'العد حسب الفئة' })}
            </button>
            <button
              className={openMode === 'single' ? 'active' : ''}
              onClick={() => setOpenMode('single')}
            >
              {t({ en: 'Type total amount', ar: 'إدخال المبلغ الإجمالي' })}
            </button>
          </div>

          {openMode === 'denom' ? (
            <div className="denom-grid">
              {DENOMINATIONS.map((d) => {
                const key = String(d.value);
                const count = openCounts[key] ?? 0;
                const subtotal = count * d.value;
                return (
                  <div className="denom-row" key={key}>
                    <div className={`denom-bill ${d.kind === 'coin' ? 'small' : ''}`}>
                      {d.label}
                    </div>
                    <span className="denom-x">×</span>
                    <input
                      type="number"
                      className="denom-input"
                      min={0}
                      step={1}
                      value={count || ''}
                      placeholder="0"
                      onChange={(e) => setOpenCount(key, parseInt(e.target.value) || 0)}
                    />
                    <div className={`denom-subtotal ${subtotal === 0 ? 'muted' : ''}`}>
                      {fmt(subtotal)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <input
                type="number"
                className="single-amount-input"
                min={0}
                step={amountStep()}
                value={openSingleAmount || ''}
                placeholder={amountText(0)}
                onChange={(e) => setOpenSingleAmount(parseFloat(e.target.value) || 0)}
                autoFocus
              />
              <div className="single-amount-currency">
                {t({ en: 'type the total in your drawer', ar: 'أدخل الإجمالي الموجود في الدرج' })}
              </div>
            </>
          )}

          <div style={{ marginTop: 14 }}>
            <label className="login-label" style={{ marginBottom: 6 }}>
              {t({ en: 'Notes (optional)', ar: 'ملاحظات (اختياري)' })}
            </label>
            <textarea
              className="shift-notes"
              value={openNotes}
              onChange={(e) => setOpenNotes(e.target.value)}
              placeholder={t({ en: 'Anything to flag for the manager? Damaged bills, missing change, etc.', ar: 'هل هناك ما تريد إبلاغ المدير به؟ أوراق تالفة، نقص في الفكة، إلخ.' })}
            />
          </div>
        </div>

        <div className="shift-side">
          <div className="shift-summary-title">{t({ en: 'Opening total', ar: 'الإجمالي الافتتاحي' })}</div>
          <div className="shift-total-card">
            <div className="shift-total-label">{t({ en: 'Counted', ar: 'المعدود' })}</div>
            <div className="shift-total-value">{fmt(total)}</div>

          </div>
          <div className="previous-shift">
            <div className="previous-shift-title">{t({ en: 'Last shift', ar: 'آخر وردية' })}</div>
            <div className="previous-shift-row"><span>{t({ en: 'Z-report', ar: 'تقرير Z' })}</span><span className="v">#Z-0431</span></div>
            <div className="previous-shift-row"><span>{t({ en: 'Closed', ar: 'أُغلقت' })}</span><span className="v">Yesterday 23:14</span></div>
            <div className="previous-shift-row"><span>{t({ en: 'Closing cash', ar: 'النقدية الختامية' })}</span><span className="v">2,847.50</span></div>
            <div className="previous-shift-row">
              <span>{t({ en: 'Variance', ar: 'الفرق' })}</span>
              <span className="v" style={{ color: 'var(--success)' }}>+2.50</span>
            </div>
          </div>
          <button
            className="btn-shift"
            onClick={handleConfirm}
            disabled={total < 0}
          >
            {t({ en: 'Open shift', ar: 'فتح الوردية' })}
            <CheckIcon size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
